import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import Layout from "components/Layout";

// Page Imports
import CompanyUserLogin from "pages/company-user-login";
import DashboardOverview from "pages/dashboard-overview";
import VendorApprovalWorkflow from "pages/vendor-approval-workflow";
import PublicVendorRegistrationForm from "pages/public-vendor-registration-form";
import PublicVendorAcknowledgement from "pages/public-vendor-acknowledgement";
import ConflictingVendors from "pages/conflicting-vendors";
import VendorMasterList from "pages/vendor-master-list";
import VendorProfileDetails from "pages/vendor-profile-details";
import VendorExcelImport from "pages/vendor-excel-import";
import SupplierRequestForm from "pages/supplier-request-form";
import SupplierRequests from "pages/supplier-requests";
import UserManagement from "pages/user-management";
import InviteManagement from "pages/invite-management";
import MisReport from "pages/mis-report";
import VendorRegularization from "pages/vendor-regularization";
import BatchDetails from "pages/vendor-regularization/batch-details/[batchId]";
import VendorRegularizationForm from "pages/vendor-regularization-form";
import PublicDocumentReplacement from "pages/public-document-replacement";
import NotFound from "pages/NotFound";

// ProtectedRoute component
function ProtectedRoute({ children, requireAdmin = false, requireAdminOrCoordinator = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/company-user-login" replace />;
  }
  
  // Check if admin-only access is required (case-insensitive)
  if (requireAdmin && user?.role?.toUpperCase() !== 'ADMIN') {
    return <Navigate to="/supplier-requests" replace />;
  }
  
  // Check if admin or coordinator access is required (case-insensitive)
  if (requireAdminOrCoordinator) {
    const role = user?.role?.toUpperCase();
    if (role !== 'ADMIN' && role !== 'COORDINATOR') {
      return <Navigate to="/supplier-requests" replace />;
    }
  }
  
  return children;
}

// RoleBasedRedirect component
function RoleBasedRedirect() {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/company-user-login" replace />;
  }
  
  const role = user?.role?.toUpperCase();
  if (role === 'ADMIN' || role === 'COORDINATOR') {
    return <Navigate to="/dashboard-overview" replace />;
  } else {
    return <Navigate to="/supplier-requests" replace />;
  }
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public Routes */}
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="/company-user-login" element={<CompanyUserLogin />} />
          <Route path="/public-vendor-registration-form" element={<PublicVendorRegistrationForm />} />
          <Route path="/public-vendor-acknowledgement" element={<PublicVendorAcknowledgement />} />
          <Route path="/vendor-regularization-form" element={<VendorRegularizationForm />} />
          <Route path="/public-document-replacement" element={<PublicDocumentReplacement />} />

          {/* Protected Routes (Wrapped in Layout) */}
          <Route element={<Layout />}>
            {/* Dashboard - Admin & Coordinator */}
            <Route path="/dashboard-overview" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <DashboardOverview />
              </ProtectedRoute>
            } />
            
            {/* Vendor Approval - Admin & Coordinator */}
            <Route path="/vendor-approval-workflow" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorApprovalWorkflow />
              </ProtectedRoute>
            } />
            
            {/* Vendor Master List - Admin & Coordinator */}
            <Route path="/vendor-master-list" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorMasterList />
              </ProtectedRoute>
            } />
            
            {/* Vendor Profile - Admin & Coordinator */}
            <Route path="/vendor-profile-details/:vendorId" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <VendorProfileDetails />
              </ProtectedRoute>
            } />
            
            {/* Excel Import - Admin Only */}
            <Route path="/vendor-excel-import" element={
              <ProtectedRoute requireAdmin={true}>
                <VendorExcelImport />
              </ProtectedRoute>
            } />
            
            {/* User Management - Admin Only */}
            <Route path="/user-management" element={
              <ProtectedRoute requireAdmin={true}>
                <UserManagement />
              </ProtectedRoute>
            } />
            
            {/* MIS Report - Admin & Coordinator */}
            <Route path="/mis-report" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <MisReport />
              </ProtectedRoute>
            } />

            {/* Invite Management - Admin & Coordinator */}
            <Route path="/invite-management" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <InviteManagement />
              </ProtectedRoute>
            } />
            
            {/* Conflicting Vendors - Admin & Coordinator */}
            <Route path="/conflicting-vendors" element={
              <ProtectedRoute requireAdminOrCoordinator={true}>
                <ConflictingVendors />
              </ProtectedRoute>
            } />
            
            {/* Vendor Regularization - Admin Only */}
            <Route path="/vendor-regularization" element={
              <ProtectedRoute requireAdmin={true}>
                <VendorRegularization />
              </ProtectedRoute>
            } />
            <Route path="/vendor-regularization/batch/:batchId" element={
              <ProtectedRoute requireAdmin={true}>
                <BatchDetails />
              </ProtectedRoute>
            } />
            
            {/* Supplier Request Form - All Authenticated Users */}
            <Route path="/supplier-request-form" element={
              <ProtectedRoute>
                <SupplierRequestForm />
              </ProtectedRoute>
            } />
            
            {/* Supplier Requests List - All Authenticated Users */}
            <Route path="/supplier-requests" element={
              <ProtectedRoute>
                <SupplierRequests />
              </ProtectedRoute>
            } />
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
